import { useState, useEffect } from 'react';
import { Target, Plus, Search, Building2, UserCheck, Check, X, Eye, Pencil, Trash2, Calendar, Users, ArrowRight } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { CastingBrief, Influencer, Client, castingRef, influencersRef, clientsRef, createDocument, getDocuments, updateDocument, deleteDocument } from '../lib/firebase';

const PLATFORM_ICONS = {
  Instagram: 'https://cdn.simpleicons.org/instagram',
  YouTube: 'https://cdn.simpleicons.org/youtube',
  TikTok: 'https://cdn.simpleicons.org/tiktok',
  Twitch: 'https://cdn.simpleicons.org/twitch',
  Snapchat: 'https://cdn.simpleicons.org/snapchat',
  X: 'https://cdn.simpleicons.org/x',
  Facebook: 'https://cdn.simpleicons.org/facebook',
  Pinterest: 'https://cdn.simpleicons.org/pinterest',
};

export default function Casting() {
  const [castings, setCastings] = useState<CastingBrief[]>([]);
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInfluencerSelectorOpen, setIsInfluencerSelectorOpen] = useState(false);
  const [isClientSelectorOpen, setIsClientSelectorOpen] = useState(false);
  const [selectedCasting, setSelectedCasting] = useState<CastingBrief | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [influencerSearch, setInfluencerSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [isStatusUpdateModalOpen, setIsStatusUpdateModalOpen] = useState(false);
  const [castingToClose, setCastingToClose] = useState<CastingBrief | null>(null);
  const [formData, setFormData] = useState<Partial<CastingBrief>>({
    name: '',
    brand: '',
    description: '',
    requirements: [],
    status: 'draft',
    selectedInfluencers: [],
    proposedInfluencers: [],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const [castingsData, influencersData, clientsData] = await Promise.all([
        getDocuments<CastingBrief>(castingRef),
        getDocuments<Influencer>(influencersRef),
        getDocuments<Client>(clientsRef),
      ]);
      setCastings(castingsData);
      setInfluencers(influencersData.filter(inf => !inf.archived));
      setClients(clientsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (selectedCasting?.id) {
        await updateDocument(castingRef, selectedCasting.id, formData);
      } else {
        await createDocument(castingRef, {
          ...formData,
          status: 'active',
          createdAt: new Date(),
          updatedAt: new Date(),
        } as CastingBrief);
      }
      await loadData();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving casting:', error);
    }
  };

  const handleDelete = async (castingId: string) => {
    try {
      await deleteDocument(castingRef, castingId);
      await loadData();
    } catch (error) {
      console.error('Error deleting casting:', error);
    }
  };

  const handleEdit = (casting: CastingBrief) => {
    setSelectedCasting(casting);
    setFormData({
      name: casting.name,
      brand: casting.brand,
      description: casting.description,
      requirements: [...casting.requirements],
      status: casting.status,
      selectedInfluencers: [...casting.selectedInfluencers],
      proposedInfluencers: [...casting.proposedInfluencers],
    });
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCasting(null);
    setFormData({
      name: '',
      brand: '',
      description: '',
      requirements: [],
      status: 'draft',
      selectedInfluencers: [],
      proposedInfluencers: [],
    });
  };

  const handleCloseCasting = async () => {
    try {
      if (!castingToClose?.id) return;

      await updateDocument(castingRef, castingToClose.id, {
        status: 'closed',
        updatedAt: new Date()
      });

      await loadData();
      setIsStatusUpdateModalOpen(false);
      setCastingToClose(null);
    } catch (error) {
      console.error('Error closing casting:', error);
    }
  };

  const handleAddRequirement = () => {
    setFormData(prev => ({
      ...prev,
      requirements: [...(prev.requirements || []), ''],
    }));
  };

  const handleRemoveRequirement = (index: number) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleRequirementChange = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      requirements: prev.requirements?.map((req, i) => i === index ? value : req) || [],
    }));
  };

  const handleSelectInfluencer = (influencer: Influencer) => {
    setFormData(prev => ({
      ...prev,
      proposedInfluencers: [
        ...(prev.proposedInfluencers || []),
        {
          influencerId: influencer.id!,
          fullName: influencer.fullName,
          status: 'pending',
        },
      ],
    }));
    setIsInfluencerSelectorOpen(false);
  };

  const handleSelectClient = (client: Client) => {
    setFormData(prev => ({
      ...prev,
      brand: client.brand_name,
    }));
    setIsClientSelectorOpen(false);
  };

  const handleUpdateInfluencerStatus = async (influencerIndex: number, status: 'accepted' | 'rejected') => {
    setFormData(prev => {
      const newProposedInfluencers = [...(prev.proposedInfluencers || [])];
      newProposedInfluencers[influencerIndex] = {
        ...newProposedInfluencers[influencerIndex],
        status
      };

      const newSelectedInfluencers = [...(prev.selectedInfluencers || [])];
      const influencerId = newProposedInfluencers[influencerIndex].influencerId;

      if (status === 'accepted' && !newSelectedInfluencers.includes(influencerId)) {
        newSelectedInfluencers.push(influencerId);
      } else if (status === 'rejected') {
        const index = newSelectedInfluencers.indexOf(influencerId);
        if (index > -1) {
          newSelectedInfluencers.splice(index, 1);
        }
      }

      return {
        ...prev,
        proposedInfluencers: newProposedInfluencers,
        selectedInfluencers: newSelectedInfluencers
      };
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'closed':
        return 'bg-green-500/20 text-green-300 border-green-500/20';
      case 'active':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20';
      default:
        return 'bg-blue-500/20 text-blue-300 border-blue-500/20';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'closed':
        return 'Terminé';
      case 'active':
        return 'En cours';
      default:
        return 'En attente';
    }
  };

  const filteredCastings = castings.filter(casting => {
    const matchesSearch = 
      casting.name.toLowerCase().includes(search.toLowerCase()) ||
      casting.brand.toLowerCase().includes(search.toLowerCase());
    
    const matchesType = typeFilter === 'all' || casting.status === typeFilter;

    return matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <Target className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-white">Casting</h1>
            <p className="text-gray-400">Gérez vos briefs de casting</p>
          </div>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Nouveau casting
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher un casting..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10"
              />
            </div>
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-full sm:w-48"
          >
            <option value="all">Tous les statuts</option>
            <option value="draft">En attente</option>
            <option value="active">En cours</option>
            <option value="closed">Terminés</option>
          </select>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredCastings.map((casting) => (
              <div key={casting.id} className="bg-gray-50 rounded-lg p-6 hover:bg-gray-100/80 transition-colors border border-gray-100 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-medium text-gray-900">{casting.name}</h3>
                      <select
                        value={casting.status}
                        onChange={async (e) => {
                          try {
                            await updateDocument(castingRef, casting.id!, {
                              status: e.target.value,
                              updatedAt: new Date()
                            });
                            await loadData();
                          } catch (error) {
                            console.error('Error updating casting status:', error);
                          }
                        }}
                        className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(casting.status)} bg-transparent cursor-pointer focus:outline-none focus:ring-2 focus:ring-accent-blue`}
                      >
                        <option value="draft" className="bg-white text-blue-600">En attente</option>
                        <option value="active" className="bg-white text-yellow-600">En cours</option>
                        <option value="closed" className="bg-white text-green-600">Terminé</option>
                      </select>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Building2 className="w-4 h-4 text-accent-blue" />
                      <span className="text-gray-600">{casting.brand}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {casting.status !== 'closed' && (
                      <button
                        onClick={() => {
                          setCastingToClose(casting);
                          setIsStatusUpdateModalOpen(true);
                        }}
                        className="px-3 py-1.5 text-sm text-green-300 bg-green-500/10 rounded-lg hover:bg-green-500/20 transition-colors hidden"
                      >
                        Marquer comme terminé
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(casting)}
                      className="p-2 text-accent-blue hover:text-accent-blue/80 rounded-lg hover:bg-dark-200"
                      title="Modifier"
                    >
                      <Pencil className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(casting.id!)}
                      className="p-2 text-red-500 hover:text-red-400 rounded-lg hover:bg-dark-200"
                      title="Supprimer"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Description</h4>
                      <p className="text-gray-600">{casting.description}</p>
                    </div>

                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Critères</h4>
                      <div className="bg-white rounded-lg p-4 border border-gray-200">
                        <ul className="space-y-2">
                          {casting.requirements.map((req, index) => (
                            <li key={index} className="flex items-center gap-2 text-gray-600">
                              <Check className="w-4 h-4 text-accent-blue flex-shrink-0" />
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Influenceurs proposés</h4>
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <div className="space-y-3">
                        {casting.proposedInfluencers.map((inf) => (
                          <div
                            key={inf.influencerId}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                          >
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-accent-blue/10 flex items-center justify-center">
                                <UserCheck className="w-4 h-4 text-accent-blue" />
                              </div>
                              <div>
                                <span className="text-gray-900 font-medium">{inf.fullName}</span>
                                {inf.notes && (
                                  <p className="text-sm text-gray-500 mt-0.5">{inf.notes}</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {inf.status === 'pending' ? (
                                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                  En attente
                                </span>
                              ) : (
                                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                  inf.status === 'accepted'
                                    ? 'bg-green-100 text-green-600 border border-green-200'
                                    : 'bg-red-100 text-red-600 border border-red-200'
                                }`}>
                                  {inf.status === 'accepted' ? 'Accepté' : 'Refusé'}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}

                        {casting.proposedInfluencers.length === 0 && (
                          <div className="text-center py-6 text-gray-500">
                            Aucun influenceur proposé
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            {filteredCastings.length === 0 && (
              <div className="text-center py-12">
                <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-gray-500" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun casting trouvé</h3>
                <p className="text-gray-600">
                  Commencez par créer un nouveau brief de casting
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog
        open={isModalOpen}
        onClose={() => {}}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-white rounded-xl max-w-2xl w-full mx-4 p-6 border border-gray-200 shadow-lg">
            <div className="absolute top-4 right-4">
              <button
                onClick={handleCloseModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">
                  {selectedCasting ? 'Modifier le casting' : 'Nouveau casting'}
                </h2>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Nom du casting
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full border-2 border-black"
                      placeholder="Ex: Casting été 2024 - Marque X"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Marque
                    </label>
                    <div className="mt-1 relative">
                      <input
                        type="text"
                        value={formData.brand}
                        className="block w-full pr-10 border-2 border-black"
                        required
                        readOnly
                        onClick={() => setIsClientSelectorOpen(true)}
                        placeholder="Sélectionner un client"
                      />
                      <button
                        type="button"
                        onClick={() => setIsClientSelectorOpen(true)}
                        className="absolute inset-y-0 right-0 px-3 flex items-center"
                      >
                        <Search className="w-4 h-4 text-gray-400" />
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="mt-1 block w-full border-2 border-black"
                      required
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Critères
                      </label>
                      <button
                        type="button"
                        onClick={handleAddRequirement}
                        className="text-sm text-indigo-600 hover:text-indigo-800"
                      >
                        + Ajouter un critère
                      </button>
                    </div>
                    <div className="space-y-2">
                      {formData.requirements?.map((req, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={req}
                            onChange={(e) => handleRequirementChange(index, e.target.value)}
                            className="flex-1 border-2 border-black"
                            placeholder="Ex: 18-25 ans, lifestyle..."
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveRequirement(index)}
                            className="text-red-500 hover:text-red-400"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Influenceurs proposés
                      </label>
                      <button
                        type="button"
                        onClick={() => setIsInfluencerSelectorOpen(true)}
                        className="text-sm text-indigo-600 hover:text-indigo-800"
                      >
                        + Ajouter un influenceur
                      </button>
                    </div>
                    <div className="space-y-2">
                      {formData.proposedInfluencers?.map((inf, index) => (
                        <div key={inf.influencerId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-accent-blue/10 flex items-center justify-center">
                              <UserCheck className="w-4 h-4 text-accent-blue" />
                            </div>
                            <span className="text-gray-900 font-medium">{inf.fullName}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {inf.status === 'pending' ? (
                              <>
                                <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                                  En attente
                                </span>
                                <button
                                  type="button"
                                  onClick={() => handleUpdateInfluencerStatus(index, 'accepted')}
                                  className="p-1.5 bg-green-100 text-green-600 rounded-lg hover:bg-green-200"
                                  title="Accepter"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleUpdateInfluencerStatus(index, 'rejected')}
                                  className="p-1.5 bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                                  title="Refuser"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                                  inf.status === 'accepted'
                                    ? 'bg-green-100 text-green-600 border border-green-200'
                                    : 'bg-red-100 text-red-600 border border-red-200'
                                }`}>
                                  {inf.status === 'accepted' ? 'Accepté' : 'Refusé'}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFormData(prev => {
                                      const newProposedInfluencers = [...(prev.proposedInfluencers || [])];
                                      newProposedInfluencers[index] = {
                                        ...newProposedInfluencers[index],
                                        status: 'pending'
                                      };
                                      
                                      const newSelectedInfluencers = [...(prev.selectedInfluencers || [])];
                                      const influencerId = newProposedInfluencers[index].influencerId;
                                      const selectedIndex = newSelectedInfluencers.indexOf(influencerId);
                                      if (selectedIndex > -1) {
                                        newSelectedInfluencers.splice(selectedIndex, 1);
                                      }
                                      
                                      return {
                                        ...prev,
                                        proposedInfluencers: newProposedInfluencers,
                                        selectedInfluencers: newSelectedInfluencers
                                      };
                                    });
                                  }}
                                  className="p-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                                  title="Réinitialiser"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                proposedInfluencers: prev.proposedInfluencers?.filter((_, i) => i !== index) || []
                              }))}
                              className="text-red-500 hover:text-red-400"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-8">
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
                  >
                    {selectedCasting ? 'Modifier' : 'Créer'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border-2 border-black rounded-lg hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Client Selector Modal */}
      <Dialog
        open={isClientSelectorOpen}
        onClose={() => setIsClientSelectorOpen(false)}
        className="fixed inset-0 z-20 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-white rounded-xl max-w-2xl w-full mx-4 p-6 border border-gray-200 shadow-lg">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsClientSelectorOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Sélectionner un client</h2>

              <div className="relative">
                <input
                  type="text"
                  value={clientSearch}
                  onChange={(e) => setClientSearch(e.target.value)}
                  placeholder="Rechercher un client..."
                  className="block w-full pr-10 pl-10 border-2 border-black"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {clients
                  .filter(client =>
                    client.brand_name.toLowerCase().includes(clientSearch.toLowerCase())
                  )
                  .map((client) => (
                    <div
                      key={client.id}
                      onClick={() => handleSelectClient(client)}
                      className="w-full flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer border border-gray-100"
                    >
                      <Building2 className="w-5 h-5 text-accent-blue mr-3" />
                      <div className="text-left">
                        <h3 className="text-gray-900 font-medium">{client.brand_name}</h3>
                        {client.contacts[0] && (
                          <p className="text-sm text-gray-500">
                            {client.contacts[0].name} • {client.contacts[0].email}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Influencer Selector Modal */}
      <Dialog
        open={isInfluencerSelectorOpen}
        onClose={() => setIsInfluencerSelectorOpen(false)}
        className="fixed inset-0 z-20 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-white rounded-xl max-w-2xl w-full mx-4 p-6 border border-gray-200 shadow-lg">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsInfluencerSelectorOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              <h2 className="text-2xl font-bold text-gray-900">Sélectionner un influenceur</h2>

              <div className="relative">
                <input
                  type="text"
                  readOnly={false}
                  value={influencerSearch}
                  onChange={(e) => setInfluencerSearch(e.target.value)}
                  placeholder="Rechercher un influenceur..."
                  className="block w-full pr-10 pl-10 border-2 border-black focus:outline-none focus:ring-2 focus:ring-accent-blue"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {influencers
                  .filter(influencer =>
                    influencer.fullName.toLowerCase().includes(influencerSearch.toLowerCase())
                  )
                  .map((influencer) => (
                    <div
                      key={influencer.id}
                      onClick={() => handleSelectInfluencer(influencer)}
                      className="w-full flex items-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer border border-gray-100"
                    >
                      <div className="text-left">
                        <h3 className="text-gray-900 font-medium">{influencer.fullName}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          {influencer.platforms.map((platform, index) => (
                            <img
                              key={index}
                              src={PLATFORM_ICONS[platform.name as keyof typeof PLATFORM_ICONS]}
                              alt={platform.name}
                              className="w-4 h-4"
                            />
                          ))}
                          <span className="text-sm text-gray-500">•</span>
                          <span className="text-sm text-gray-500">{influencer.platforms.map(p => p.audience).join(' • ')}</span>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Status Update Modal */}
      <Dialog
        open={isStatusUpdateModalOpen}
        onClose={() => setIsStatusUpdateModalOpen(false)}
        className="fixed inset-0 z-20 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-30" />
          <div className="relative bg-white rounded-xl max-w-md w-full mx-4 p-6 border border-gray-200 shadow-lg">
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsStatusUpdateModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <Check className="w-6 h-6 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Terminer le casting
              </h2>
              <p className="text-gray-600 mb-6">
                Êtes-vous sûr de vouloir marquer ce casting comme terminé ? Cette action ne peut pas être annulée.
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={handleCloseCasting}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                >
                  Confirmer
                </button>
                <button
                  onClick={() => setIsStatusUpdateModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}